/**
 * Coming Soon Section
 *
 * Unified roadmap teaser with a single "Keep me in the loop" email capture.
 * Lists upcoming features (gift cards, SMS, Business CRM) as bullet points
 * so visitors see the product vision without re-teasing already-shipped gifts
 * (e-cards, mailed cards, flowers).
 *
 * Placed after the Final CTA to avoid competing with the primary conversion goal.
 *
 * Spam prevention:
 * - Hidden honeypot field (bots fill it, humans don't)
 * - Server-side rate limiting (5 req/hr/IP via PublicApi policy)
 * - Server-side email validation and idempotent duplicate handling
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Group,
  List,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconCheck, IconRocket } from '@tabler/icons-react';
import { brandColors, commonStyles, semanticColors } from '../../config/theme';
import { landingContent } from '../../content/landingContent';
import { submitProductInterest } from '../../services/publicTemplatesService';

/** Backend product name stored when a visitor subscribes to the roadmap */
const ROADMAP_PRODUCT_NAME = 'Roadmap Updates';

interface ComingSoonSectionProps {
  /** When true, renders title/subtitle in white for use on dark backgrounds */
  dark?: boolean;
}

/**
 * Renders the "Even more on the way" section with a feature list and single email form.
 */
export const ComingSoonSection = ({ dark = false }: ComingSoonSectionProps) => {
  const { title, subtitle, features, ctaLabel, ctaPlaceholder, successMessage } =
    landingContent.sections.comingSoon;

  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!email.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      await submitProductInterest(email.trim(), ROADMAP_PRODUCT_NAME, honeypot);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="lg">
      <Title order={2} ta="center" c={dark ? 'white' : semanticColors.text.title}>
        {title}
      </Title>
      <Text ta="center" c={dark ? 'gray.3' : semanticColors.text.secondary} size="md" maw={520} mx="auto">
        {subtitle}
      </Text>

      <Paper
        radius="lg"
        p="xl"
        withBorder
        style={{ ...commonStyles.warmCard }}
        maw={600}
        mx="auto"
        w="100%"
      >
        <Stack gap="lg">
          {/* Feature roadmap list */}
          <List
            spacing="sm"
            size="sm"
            icon={
              <ThemeIcon size={22} radius="xl" color={brandColors.warmAccent} variant="light">
                <IconRocket size={13} />
              </ThemeIcon>
            }
          >
            {features.map((feature) => {
              const [label, ...rest] = feature.split(' — ');
              const description = rest.join(' — ');
              return (
                <List.Item key={label}>
                  <Text component="span" fw={600} size="sm">{label}</Text>
                  {description && (
                    <Text component="span" size="sm" c={semanticColors.text.secondary}> — {description}</Text>
                  )}
                </List.Item>
              );
            })}
          </List>

          {/* Single email capture form */}
          {submitted ? (
            <Group gap="xs" justify="center">
              <ThemeIcon size={22} radius="xl" color={brandColors.success} variant="light">
                <IconCheck size={14} />
              </ThemeIcon>
              <Text size="sm" c="green.7" >{successMessage}</Text>
            </Group>
          ) : (
            <Stack gap="xs">
              <Group gap="xs" wrap="nowrap">
                <TextInput
                  placeholder={ctaPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  size="sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                />
                <Button
                  size="sm"
                  variant="light"
                  color={brandColors.warmAccent}
                  onClick={handleSubmit}
                  loading={submitting}
                >
                  {ctaLabel}
                </Button>
              </Group>

              {/* Honeypot — hidden from users, filled by bots */}
              <Box
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                aria-hidden="true"
              >
                <TextInput
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.currentTarget.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </Box>

              {error && <Text size="xs" c={brandColors.danger}>{error}</Text>}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};
